import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import EnterpriseTypes from '../../Redux/transition/enterprise/enterprise.reducer';
import WebhookTypes from '../../Redux/transition/webhook/webhook.reducer';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Loading from "../../Components/Loading";
import CustomToast from '../../Components/CustomToast';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { useTranslation } from "react-i18next";
import { useDateFormatter } from '../../Components/FormatDateTime';
import {
  parseWebhookInterests,
  serializeWebhookInterests,
} from '../../constants/webhookInterests';
import { getWebhookEventTypes } from '../../Service/api';
import "../../Styles/pages/settings.scss";

function Api() {
  const { t } = useTranslation("common");
  const { formatDateTime } = useDateFormatter();
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [state, setState] = useState({
    showAuthValue: false,
    loading: false,
    loading2: false,
    webhookUrl: '',
    webhookInterests: [],
    authHeaderName: '',
    authHeaderValue: '',
  })
  const [toastData, setToastData] = useState({
    summary: '', severity: '', detail: '', life: 0
  });
  const [toastTrigger, setToastTrigger] = useState(false);
  const [createInitiated, setCreateInitiated] = useState(false);
  const [createWebhookInitiated, setCreateWebhookInitiated] = useState(false);
  const [updateWebhookInitiated, setUpdateWebhookInitiated] = useState(false);
  const [toggleWebhookInitiated, setToggleWebhookInitiated] = useState(false);
  const [toggleConfirmVisible, setToggleConfirmVisible] = useState(false);
  const [pendingToggleAction, setPendingToggleAction] = useState(null);
  const [webhookEventTypes, setWebhookEventTypes] = useState([]);
  const [fetchingEventTypes, setFetchingEventTypes] = useState(true);

  const defMembership = useSelector((state) => state.defMembership.defMembership);
  const enterprise = useSelector((state) => state.enterprise.enterprise);
  const fetchingEnterprise = useSelector((state) => state.enterprise.fetchingEnterprise);
  const creating = useSelector((state) => state.enterprise.creating);
  const generating = useSelector((state) => state.enterprise.generating);
  const errorCreating = useSelector((state) => state.enterprise.errorCreating);

  const webhook = useSelector((state) => state.webhook.webhook);
  const fetchingWebhook = useSelector((state) => state.webhook.fetchingWebhook);
  const creatingWebhook = useSelector((state) => state.webhook.creating);
  const updatingWebhook = useSelector((state) => state.webhook.updating);
  const togglingWebhook = useSelector((state) => state.webhook.toggling);
  const errorCreatingWebhook = useSelector((state) => state.webhook.errorCreating);
  const errorUpdatingWebhook = useSelector((state) => state.webhook.errorUpdating);
  const errorTogglingWebhook = useSelector((state) => state.webhook.errorToggling);

  const dispatch = useDispatch();
  const getCredentials = useCallback(businessId => { dispatch(EnterpriseTypes.enterpriseRequest(businessId)) }, [dispatch]);
  const createCredentials = useCallback((businessId) => { dispatch(EnterpriseTypes.createCredentialsRequest(businessId)) }, [dispatch]);
  const generate = useCallback((businessId) => { dispatch(EnterpriseTypes.generateRequest(businessId)) }, [dispatch]);
  const getWebhook = useCallback((businessId) => { dispatch(WebhookTypes.webhookRequest(businessId)) }, [dispatch]);
  const createWebhook = useCallback((businessId, url, interests, authHeaderName, authHeaderValue) => {
    dispatch(WebhookTypes.createWebhookRequest(businessId, url, interests, authHeaderName, authHeaderValue))
  }, [dispatch]);
  const updateWebhook = useCallback((webhookId, businessId, url, interests, authHeaderName, authHeaderValue) => {
    dispatch(WebhookTypes.updateWebhookRequest(webhookId, businessId, url, interests, authHeaderName, authHeaderValue))
  }, [dispatch]);
  const toggleWebhookStatus = useCallback((webhookId) => {
    dispatch(WebhookTypes.toggleWebhookRequest(webhookId))
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const fetchEventTypes = async () => {
      setFetchingEventTypes(true);
      const response = await getWebhookEventTypes();

      if (cancelled) {
        return;
      }

      if (response.ok && Array.isArray(response.data)) {
        setWebhookEventTypes(response.data);
      } else {
        setWebhookEventTypes([]);
      }

      setFetchingEventTypes(false);
    };

    fetchEventTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatInterestsForDisplay = useCallback((interests) => {
    const parsedInterests = parseWebhookInterests(interests);
    if (!parsedInterests.length) {
      return '—';
    }

    return parsedInterests.join(', ');
  }, []);

  const showToast = (summary, severity, detail) => {
    setToastData({
      summary,
      severity,
      detail,
      life: 3000,
    });
    setToastTrigger(true)
  }

  const copyToClipboard = (value) => {
    if (!value) {
      return;
    }
    navigator.clipboard.writeText(String(value));
    showToast(t("success"), "success", t("api_settings.copy_success"));
  };

  const renderCopyableCell = (value, displayValue = value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ wordBreak: 'break-all', flex: 1 }}>{displayValue || '—'}</span>
      {value && (
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text p-button-info"
          onClick={() => copyToClipboard(value)}
          aria-label={t("api_settings.copy")}
        />
      )}
    </div>
  );

  const businessId = defMembership?.business?.id;
  const hasCredentials = !!enterprise;
  const hasWebhook = !!webhook;
  const showWebhookLoading = businessId && fetchingWebhook !== false;

  useEffect(() => {
    if (!businessId) {
      return;
    }
    getCredentials(businessId);
    getWebhook(businessId);
  }, [businessId, getCredentials, getWebhook])

  useEffect(() => {
    if (!generating && state.loading2) {
      setState((prev) => ({ ...prev, loading2: false }))
    }
  }, [generating, state.loading2])

  useEffect(() => {
    if (webhook) {
      setState((prev) => ({
        ...prev,
        webhookUrl: webhook.url || '',
        webhookInterests: parseWebhookInterests(webhook.interests),
        authHeaderName: webhook.authHeaderName || '',
        authHeaderValue: webhook.authHeaderValue || '',
      }));
    }
  }, [webhook])

  useEffect(() => {
    if (!createInitiated || creating) {
      return;
    }

    if (errorCreating) {
      showToast(t("failed"), "error", t("api_settings.create_error"))
    } else if (enterprise) {
      showToast(t("success"), "success", t("api_settings.create_success"))
    }
    setCreateInitiated(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, errorCreating, enterprise, createInitiated])

  useEffect(() => {
    if (!createWebhookInitiated || creatingWebhook) {
      return;
    }

    if (errorCreatingWebhook) {
      showToast(t("failed"), "error", t("api_settings.webhook.create_error"))
    } else if (webhook) {
      showToast(t("success"), "success", t("api_settings.webhook.create_success"))
    }
    setCreateWebhookInitiated(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingWebhook, errorCreatingWebhook, webhook, createWebhookInitiated])

  useEffect(() => {
    if (!updateWebhookInitiated || updatingWebhook) {
      return;
    }

    if (errorUpdatingWebhook) {
      showToast(t("failed"), "error", t("api_settings.webhook.update_error"))
    } else if (webhook) {
      showToast(t("success"), "success", t("api_settings.webhook.update_success"))
    }
    setUpdateWebhookInitiated(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingWebhook, errorUpdatingWebhook, webhook, updateWebhookInitiated])

  useEffect(() => {
    if (!toggleWebhookInitiated || togglingWebhook) {
      return;
    }

    if (errorTogglingWebhook) {
      showToast(t("failed"), "error", t("api_settings.webhook.toggle_error"))
    } else if (webhook) {
      const successMessage = pendingToggleAction === 'enable'
        ? t("api_settings.webhook.toggle_enable_success")
        : t("api_settings.webhook.toggle_disable_success");
      showToast(t("success"), "success", successMessage)
    }
    setToggleWebhookInitiated(false)
    setPendingToggleAction(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [togglingWebhook, errorTogglingWebhook, webhook, toggleWebhookInitiated, pendingToggleAction])

  const generatePassword = () => {
    generate(defMembership.business.id)
    setState((state) => ({ ...state, loading2: true }))
  }

  const handleCreateCredentials = () => {
    createCredentials(defMembership.business.id)
    setCreateInitiated(true)
  }

  const validateWebhookForm = (url) => {
    if (!url) {
      showToast(t("failed"), "error", t("api_settings.webhook.url_required"))
      return false;
    }
    if (state.authHeaderName.trim() && !state.authHeaderValue.trim()) {
      showToast(t("failed"), "error", t("api_settings.webhook.auth_header_value_required"))
      return false;
    }
    return true;
  }

  const handleCreateWebhook = () => {
    const url = state.webhookUrl.trim();
    if (!validateWebhookForm(url)) {
      return;
    }
    createWebhook(
      defMembership.business.id,
      url,
      serializeWebhookInterests(state.webhookInterests),
      state.authHeaderName.trim(),
      state.authHeaderValue.trim()
    )
    setCreateWebhookInitiated(true)
  }

  const handleUpdateWebhook = () => {
    const url = state.webhookUrl.trim();
    if (!validateWebhookForm(url)) {
      return;
    }
    updateWebhook(
      webhook.id,
      defMembership.business.id,
      url,
      serializeWebhookInterests(state.webhookInterests),
      state.authHeaderName.trim(),
      state.authHeaderValue.trim()
    )
    setUpdateWebhookInitiated(true)
  }

  const isWebhookDisabled = (disabledOn) => !!disabledOn;

  const openToggleConfirm = (action) => {
    setPendingToggleAction(action);
    setToggleConfirmVisible(true);
  };

  const handleToggleConfirm = () => {
    if (!webhook?.id) {
      return;
    }
    setToggleConfirmVisible(false);
    toggleWebhookStatus(webhook.id);
    setToggleWebhookInitiated(true);
  };

  const renderWebhookStatus = (rowData) => {
    const disabled = isWebhookDisabled(rowData.disabledOn);
    const statusLabel = disabled
      ? t("api_settings.webhook.disabled")
      : t("api_settings.webhook.active");

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <span>{statusLabel}</span>
        {disabled ? (
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text p-button-success"
            loading={togglingWebhook}
            disabled={togglingWebhook}
            tooltip={t("api_settings.webhook.enable")}
            tooltipOptions={{
              position: "bottom",
              mouseTrack: true,
              mouseTrackTop: 15,
            }}
            onClick={(e) => {
              e.stopPropagation();
              openToggleConfirm('enable');
            }}
            aria-label={t("api_settings.webhook.enable")}
          />
        ) : (
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text p-button-danger"
            loading={togglingWebhook}
            disabled={togglingWebhook}
            tooltip={t("api_settings.webhook.disable")}
            tooltipOptions={{
              position: "bottom",
              mouseTrack: true,
              mouseTrackTop: 15,
            }}
            onClick={(e) => {
              e.stopPropagation();
              openToggleConfirm('disable');
            }}
            aria-label={t("api_settings.webhook.disable")}
          />
        )}
      </div>
    );
  };

  const credentialRows = useMemo(
    () => (enterprise ? [{ ...enterprise, showClientSecret }] : []),
    [enterprise, showClientSecret]
  );
  const webhookRows = useMemo(
    () => (webhook ? [{ ...webhook, showAuthValue: state.showAuthValue }] : []),
    [webhook, state.showAuthValue]
  );

  const renderWebhookForm = (submitLabel, onSubmit, loading) => (
    <>
      <div className="p-field" style={{ marginBottom: '1rem', maxWidth: '32rem' }}>
        <label htmlFor="webhookUrl">{t("api_settings.webhook.url")}</label>
        <InputText
          id="webhookUrl"
          value={state.webhookUrl}
          onChange={(e) => setState((prev) => ({ ...prev, webhookUrl: e.target.value }))}
          style={{ width: '100%' }}
        />
      </div>
      <div className="p-field" style={{ marginBottom: '1rem', maxWidth: '32rem' }}>
        <label htmlFor="webhookInterests">{t("api_settings.webhook.interests")}</label>
        <MultiSelect
          id="webhookInterests"
          value={state.webhookInterests}
          options={webhookEventTypes}
          placeholder={t("api_settings.webhook.interests_placeholder")}
          filter
          display="chip"
          loading={fetchingEventTypes}
          disabled={fetchingEventTypes}
          style={{ width: '100%' }}
          onChange={(e) => setState((prev) => ({ ...prev, webhookInterests: e.value || [] }))}
        />
      </div>
      <div className="p-field" style={{ marginBottom: '1rem', maxWidth: '32rem' }}>
        <label htmlFor="authHeaderName">{t("api_settings.webhook.auth_header_name")}</label>
        <InputText
          id="authHeaderName"
          value={state.authHeaderName}
          onChange={(e) => setState((prev) => ({ ...prev, authHeaderName: e.target.value }))}
          style={{ width: '100%' }}
        />
      </div>
      <div className="p-field" style={{ marginBottom: '1rem', maxWidth: '32rem' }}>
        <label htmlFor="authHeaderValue">{t("api_settings.webhook.auth_header_value")}</label>
        <InputText
          id="authHeaderValue"
          type="password"
          value={state.authHeaderValue}
          onChange={(e) => setState((prev) => ({ ...prev, authHeaderValue: e.target.value }))}
          style={{ width: '100%' }}
        />
      </div>
      <Button label={submitLabel} onClick={onSubmit} loading={loading} />
    </>
  );

  return (
    <>
      <CustomToast
        {...toastData}
        trigger={toastTrigger}
        handleActionsAfterToast={() => {
          setToastTrigger(false);
        }}
        handleActionsAfterErrorToast={() => {
          setToastTrigger(false)
        }}
      />
      <ConfirmDialog
        visible={toggleConfirmVisible}
        onHide={() => {
          setToggleConfirmVisible(false);
          setPendingToggleAction(null);
        }}
        header={pendingToggleAction === 'enable'
          ? t("api_settings.webhook.toggle_enable_header")
          : t("api_settings.webhook.toggle_disable_header")}
        message={pendingToggleAction === 'enable'
          ? t("api_settings.webhook.toggle_enable_message")
          : t("api_settings.webhook.toggle_disable_message")}
        accept={handleToggleConfirm}
        reject={() => {
          setToggleConfirmVisible(false);
          setPendingToggleAction(null);
        }}
        acceptLabel={t("common.ok")}
        rejectLabel={t("common.no")}
      />
      <div className="card p-card">
        <span className="info-text">{t("api_settings.title")}</span>
        <hr className="settings-section-divider" />
        {fetchingEnterprise ? (
          <Loading />
        ) : (
          <>
            {hasCredentials ? (
              <Button label={t("api_settings.generate")} style={{ marginBottom: '1rem' }} onClick={() => generatePassword()} loading={generating} />
            ) : (
              <Button label={t("api_settings.create")} style={{ marginBottom: '1rem' }} onClick={handleCreateCredentials} loading={creating} />
            )}
            <DataTable value={credentialRows} showGridlines responsiveLayout="scroll">
              <Column
                field="clientId"
                header={t("api_settings.client_id")}
                body={(rowData) => renderCopyableCell(rowData.clientId)}
              ></Column>
              <Column
                field="clientSecret"
                header={t("api_settings.password")}
                body={(rowData) => (
                  <>
                    <span style={{ wordBreak: 'break-all' }}>
                      {rowData.clientSecret
                        ? (rowData.showClientSecret ? rowData.clientSecret : "**************")
                        : '—'}
                    </span>
                    {rowData.clientSecret && (
                      <>
                        <Button
                          icon="pi pi-copy"
                          className="p-button-rounded p-button-text p-button-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(rowData.clientSecret);
                          }}
                          aria-label={t("api_settings.copy")}
                        />
                        <Button
                          icon={rowData.showClientSecret ? 'pi pi-eye-slash' : "pi pi-eye"}
                          className="p-button-rounded p-button-text p-button-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowClientSecret((prev) => !prev);
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              ></Column>
              <Column
                field="businessKey"
                header={t("api_settings.business_key")}
                body={(rowData) => renderCopyableCell(rowData.businessKey)}
              ></Column>
            </DataTable>
          </>
        )}
      </div>

      <div className="card p-card" style={{ marginTop: '1rem' }}>
        <span className="info-text">{t("api_settings.webhook.title")}</span>
        <hr className="settings-section-divider" />
        {showWebhookLoading ? (
          <Loading />
        ) : (
          <>
            {hasWebhook ? (
              <>
                <DataTable value={webhookRows} showGridlines responsiveLayout="scroll">
                  <Column field="url" header={t("api_settings.webhook.url")} body={(rowData) => (
                    <span style={{ wordBreak: 'break-all' }}>{rowData.url}</span>
                  )}></Column>
                  <Column field="authHeaderName" header={t("api_settings.webhook.auth_header_name")} body={(rowData) => rowData.authHeaderName || '—'}></Column>
                  <Column
                    field="authHeaderValue"
                    header={t("api_settings.webhook.auth_header_value")}
                    body={(rowData) => (
                      <>
                        <p style={{ width: '80%', display: 'flex', wordBreak: 'break-all' }}>
                          {rowData.authHeaderValue
                            ? (rowData.showAuthValue ? rowData.authHeaderValue : "**************")
                            : '—'}
                        </p>
                        {rowData.authHeaderValue && (
                          <Button
                            icon={rowData.showAuthValue ? 'pi pi-eye-slash' : "pi pi-eye"}
                            className="p-button-rounded p-button-text p-button-info"
                            onClick={(e) => {
                              e.stopPropagation();
                              setState((prev) => ({ ...prev, showAuthValue: !prev.showAuthValue }));
                            }}
                          />
                        )}
                      </>
                    )}
                  ></Column>
                  <Column
                    field="disabledOn"
                    header={t("api_settings.webhook.status")}
                    body={renderWebhookStatus}
                  ></Column>
                  <Column
                    field="disabledOn"
                    header={t("api_settings.webhook.disabled_on")}
                    body={(rowData) => rowData.disabledOn ? formatDateTime(rowData.disabledOn) : '—'}
                  ></Column>
                  <Column
                    field="interests"
                    header={t("api_settings.webhook.interests")}
                    body={(rowData) => formatInterestsForDisplay(rowData.interests)}
                  ></Column>
                </DataTable>
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ marginTop: 0 }}>{t("api_settings.webhook.update")}</h4>
                  {renderWebhookForm(
                    t("api_settings.webhook.save"),
                    handleUpdateWebhook,
                    updatingWebhook
                  )}
                </div>
              </>
            ) : (
              <>
                <p style={{ marginBottom: '1rem' }}>{t("api_settings.webhook.no_webhook")}</p>
                {renderWebhookForm(
                  t("api_settings.webhook.create"),
                  handleCreateWebhook,
                  creatingWebhook
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default Api
